import { observer } from "mobx-react-lite"

import type { CollectFeesStore } from "@/domains/thick-v1/stores/collect-fees-store"

import { CollectFeesView } from "./collect-fees-view"

/**
 * Observer wrapper around `CollectFeesStore`.
 *
 * Maps store state + actions to the plain DTO props consumed by
 * `CollectFeesView`. The composite owns zero mutable state of its own — the
 * store is the single source of truth. No `useEffect`, no `useParams`; the
 * enclosing `ManagePositionPageComposite` has already pushed `activeTokenId`
 * into the store base so this observer just renders the current resolved
 * position.
 */
export const CollectFeesComposite = observer(({ store }: { store: CollectFeesStore }) => {
  const status = store.resolvedStatus
  if (status.kind !== "found") {
    return (
      <div className="rounded-lg border border-p400-light bg-p400 p-4 text-t300 text-sm">
        {status.kind === "loading" && "Loading position..."}
        {status.kind === "missing" && "This position was not found or is not owned by the connected wallet."}
        {status.kind === "idle" && "Select a position to continue."}
      </div>
    )
  }

  const position = status.position
  const fee0 =
    position.pendingFees0 === undefined ? "—" : position.pool.token0.formatUnits(position.pendingFees0)
  const fee1 =
    position.pendingFees1 === undefined ? "—" : position.pool.token1.formatUnits(position.pendingFees1)

  return (
    <CollectFeesView
      token0Symbol={position.pool.token0.symbol}
      token1Symbol={position.pool.token1.symbol}
      fee0Display={fee0}
      fee1Display={fee1}
      fee0Usd={position.pendingFees0Usd}
      fee1Usd={position.pendingFees1Usd}
      claimableFeesUsd={position.claimableFeesUsd}
      canSubmit={store.canSubmit}
      isSubmitting={store.isSubmitting}
      submissionError={store.submissionError}
      onSubmit={() => void store.submit()}
      nfpmTokenIdPending={position.nfpmTokenIdPending}
    />
  )
})
